/**
 * Phase 262 — Attendance Blitz Deck.
 *
 * Single-card swipe interface for coaches/employees to log attendance
 * rapidly during high-intensity training sessions.  Each card shows ONE
 * student; tap "Present" or "Absent" to record + auto-advance.  When the
 * final student is logged, the deck calls the EXISTING `/api/attendance/bulk`
 * endpoint with the full batch — no new backend route.
 *
 * Honest UX:
 *   • Empty roster → friendly empty state, no fake "starting…" loading.
 *   • Backend errors surface verbatim via toast; the deck stays at the
 *     final card so the coach can retry without re-tapping every student.
 *   • Per-row undo: previous card always reachable via the "← Undo" link.
 *
 * Props:
 *   students       — Array of {id, name, ...}.  Required.  Order = deck order.
 *   date           — YYYY-MM-DD.  Defaults to today.
 *   batchId        — Optional, logged for audit (not part of /attendance/bulk).
 *   endpoint       — Backend endpoint to POST the bulk payload to.  Defaults
 *                    to `/attendance/bulk`.  Coach/employee flows pass
 *                    `/employee/attendance/bulk` for proper role gating.
 *   onComplete     — Callback fired once the bulk endpoint resolves.
 *   onClose        — Callback fired when the user dismisses mid-deck.
 */
import { useState, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Check, X, ChevronLeft, Loader2, CheckCheck, AlertTriangle, Users,
} from 'lucide-react';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function AttendanceBlitzDeck({
  students = [],
  date,
  batchId = null,
  endpoint = '/attendance/bulk',
  onComplete,
  onClose,
}) {
  const sessionDate = date || todayISO();
  const [idx, setIdx] = useState(0);
  const [records, setRecords] = useState([]);    // [{student_id, status}]
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);

  const total = students.length;
  const current = students[idx] || null;
  const progressPct = useMemo(() => (total === 0 ? 0 : Math.round((idx / total) * 100)), [idx, total]);

  const submitBulk = useCallback(async (allRecords) => {
    setSaving(true);
    setError(null);
    try {
      // Phase 262 — calls the EXISTING /api/attendance/bulk endpoint, not a
      // new one.  Each record: {student_id, status, method: 'blitz'}.
      const payload = {
        date: sessionDate,
        records: allRecords.map((r) => ({
          student_id: r.student_id,
          status: r.status,
          method: 'blitz',
        })),
      };
      await api.post(endpoint, payload);
      setSavedOk(true);
      toast.success(`Logged attendance for ${allRecords.length} student(s).`);
      onComplete?.(allRecords);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Bulk attendance failed.';
      setError(msg);
      toast.error(`Couldn't save attendance: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [sessionDate, onComplete, endpoint]);

  const recordStatus = useCallback((status) => {
    if (!current) return;
    const next = [...records.filter((r) => r.student_id !== current.id),
                   { student_id: current.id, status }];
    setRecords(next);
    if (idx + 1 < total) {
      setIdx(idx + 1);
    } else {
      submitBulk(next);
    }
  }, [current, records, idx, total, submitBulk]);

  const undo = useCallback(() => {
    if (idx === 0) return;
    const previousStudentId = students[idx - 1]?.id;
    setRecords((r) => r.filter((x) => x.student_id !== previousStudentId));
    setIdx(idx - 1);
    setError(null);
    setSavedOk(false);
  }, [idx, students]);

  const retry = useCallback(() => {
    submitBulk(records);
  }, [records, submitBulk]);

  // ── Empty roster ──
  if (total === 0) {
    return (
      <Card data-testid="attendance-blitz-deck-empty" className="max-w-md mx-auto">
        <CardContent className="p-6 text-center space-y-2">
          <Users className="h-6 w-6 text-slate-400 mx-auto" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            No students in this roster.
          </p>
          <p className="text-xs text-muted-foreground">
            Add students to the batch before starting a Blitz session.
          </p>
          {onClose && (
            <Button variant="outline" size="sm" onClick={onClose} data-testid="attendance-blitz-close-empty">
              Close
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Saving / completed states ──
  if (saving || savedOk || error) {
    return (
      <Card data-testid="attendance-blitz-deck-finish" className="max-w-md mx-auto">
        <CardContent className="p-6 text-center space-y-3">
          {saving && (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-amber-500 mx-auto" />
              <p className="text-sm font-medium">Saving {records.length} record(s)…</p>
            </>
          )}
          {savedOk && !saving && (
            <>
              <CheckCheck className="h-7 w-7 text-emerald-500 mx-auto" />
              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                Blitz complete — {records.length} student(s) logged for {sessionDate}.
              </p>
              {onClose && (
                <Button variant="outline" size="sm" onClick={onClose} data-testid="attendance-blitz-close-done">
                  Done
                </Button>
              )}
            </>
          )}
          {error && !saving && (
            <>
              <AlertTriangle className="h-6 w-6 text-rose-500 mx-auto" />
              <div className="space-y-1">
                <p className="text-sm font-bold text-rose-700 dark:text-rose-300">Save failed</p>
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button size="sm" onClick={retry} data-testid="attendance-blitz-retry">Retry</Button>
                {onClose && (
                  <Button variant="outline" size="sm" onClick={onClose} data-testid="attendance-blitz-close-err">
                    Cancel
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Active deck ──
  return (
    <Card
      className="max-w-md mx-auto border-2 border-amber-200 dark:border-amber-900/60"
      data-testid="attendance-blitz-deck"
      data-current-index={idx}
    >
      <CardContent className="p-5 space-y-4 text-center">
        {/* Progress strip */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-slate-500 tracking-wider">
            <span>Roster {idx + 1} / {total}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
            <div
              className="h-1 bg-amber-500 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="attendance-blitz-progress-bar"
            />
          </div>
        </div>

        {/* Current student */}
        <div className="py-3 space-y-1" data-testid="attendance-blitz-current-student">
          <p className="text-base font-bold text-slate-900 dark:text-slate-100 truncate">
            {current.name || '—'}
          </p>
          {(current.batch || current.sport) && (
            <p className="text-[11px] text-muted-foreground">
              {current.batch || ''}{current.batch && current.sport ? ' • ' : ''}{current.sport || ''}
            </p>
          )}
          <p className="text-[9px] font-mono text-slate-400">{current.id}</p>
        </div>

        {/* Present / Absent */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => recordStatus('absent')}
            className="h-16 flex-col gap-0.5 border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-700 dark:text-rose-200"
            data-testid="attendance-blitz-absent-btn"
          >
            <X className="h-5 w-5" />
            <span className="text-[11px] uppercase font-bold tracking-wider">Absent</span>
          </Button>
          <Button
            type="button"
            onClick={() => recordStatus('present')}
            className="h-16 flex-col gap-0.5 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="attendance-blitz-present-btn"
          >
            <Check className="h-5 w-5" />
            <span className="text-[11px] uppercase font-bold tracking-wider">Present</span>
          </Button>
        </div>

        {/* Undo + Close */}
        <div className="flex items-center justify-between pt-1 text-[11px]">
          <button
            type="button"
            onClick={undo}
            disabled={idx === 0}
            className="flex items-center gap-1 text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="attendance-blitz-undo"
          >
            <ChevronLeft className="h-3 w-3" /> Undo
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-slate-500 hover:text-rose-700 dark:hover:text-rose-300"
              data-testid="attendance-blitz-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
